<template>
  <!-- eslint-disable vue/no-v-html -->
  <div class="chat-container">
    <div class="chat-header">
      <h3>Suporte Energy2Go</h3>
      <p>Pergunte sobre aluguel de baterias, pagamento e mais</p>
    </div>

    <div
      ref="messagesContainer"
      class="chat-messages"
    >
      <div
        v-for="(msg, idx) in messages"
        :key="idx"
        :class="['message', msg.sender]"
      >
        <div class="message-sender">
          {{ msg.senderName }}
        </div>
        <div
          class="message-content"
          v-html="formatMessage(msg.text)"
        ></div>
        <div class="message-time">
          {{ msg.time }}
        </div>
      </div>

      <div
        v-if="isLoading"
        class="message bot"
      >
        <div class="message-content typing">
          <span ></span><span ></span><span ></span>
        </div>
      </div>
    </div>

    <div class="chat-input">
      <input
        ref="inputField"
        v-model="inputText"
        :disabled="isLoading"
        placeholder="Digite sua mensagem..."
        @keyup.enter="sendMessage"
      >
      <BaseButton
        bare
        variant=""
        :disabled="isLoading || !inputText.trim()"
        @click="sendMessage"
      >
        Enviar
      </BaseButton>
    </div>
  </div>
</template>

<script>
  import { TAKE_BATTERY_SUCCESS } from '@/constants.js'
  import { marked } from 'marked'
  import DOMPurify from 'dompurify'
  import BaseButton from '@/components/ui/BaseButton.vue'

  export default {
    name: 'ChatBot',
    components: {
      BaseButton
    },

    props: {
      cableType: {
        type: String,
        default: null
      },
      transactionId: {
        type: String,
        default: null
      },
      userToken: {
        type: String,
        default: null
      },
      pointName: {
        type: String,
        default: null
      },
      userPosition: {
        type: String,
        default: null
      },
      signature: {
        type: Boolean,
        default: false
      },
      user: {
        type: Object,
        default: null
      },
      pixPayed: {
        type: Boolean,
        default: false
      },
      lastTransactionState: {
        type: String,
        default: null
      }
    },

    data() {
      return {
        messages: [],
        inputText: '',
        isLoading: false,
        sessionId: null
      }
    },

    mounted() {
      this.loadMessages()
      this.sessionId = sessionStorage.getItem('session_id')
      this.$nextTick(() => {
        if (this.$refs.inputField) {
          this.$refs.inputField.focus()
        }
      })
    },

    methods: {
      loadMessages() {
        const saved = localStorage.getItem('chatbot-messages')

        if (saved) {
          this.messages = JSON.parse(saved)
        } else {
          this.messages = [
            {
              text: this.$t('chatbot.greeting'),
              sender: 'bot',
              senderName: 'IA',
              time: this.getCurrentTime()
            }
          ]
        }
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },

      saveMessages() {
        localStorage.setItem('chatbot-messages', JSON.stringify(this.messages))
      },

      formatMessage(text) {
        if (!text && text !== 0) return ''

        try {
          // Convert plain URLs to markdown links before parsing
          let processedText = String(text)
          const urlRegex = /(https?:\/\/[^\s<>&\"]+)/g
          processedText = processedText.replace(urlRegex, '[$&]($&)')

          const html = marked.parse(processedText, { gfm: true, breaks: true })
          const clean = DOMPurify.sanitize(html)
          try {
            const parser = new DOMParser()
            const doc = parser.parseFromString(clean, 'text/html')
            const anchors = doc.querySelectorAll('a')
            anchors.forEach(a => {
              const href = a.getAttribute('href')
              if (!href) return
              a.setAttribute('target', '_blank')
              a.setAttribute('rel', 'noopener noreferrer')
            })
            return doc.body.innerHTML
          } catch (e) {
            return clean
          }
        } catch (e) {
          return DOMPurify.sanitize(String(text))
        }
      },

      async sendMessage() {
        if (!this.inputText.trim() || this.isLoading) return
        const senderName = 'Você'
        this.messages.push({
          text: this.inputText,
          sender: 'user',
          senderName: senderName,
          time: this.getCurrentTime()
        })

        this.saveMessages()

        this.isLoading = true

        this.$nextTick(() => {
          this.scrollToBottom()
        })

        try {
          const username = import.meta.env.VITE_CHATBOT_USERNAME
          const password = import.meta.env.VITE_CHATBOT_PASSWORD
          const webhookUrl = import.meta.env.VITE_CHATBOT_WEBHOOK_URL

          if (!webhookUrl) {
            throw new Error('Webhook URL não está configurada.')
          }

          const cableMap = {
            0: 'lightning',
            1: 'micro_usb',
            2: 'usb_c'
          }
          const cableSelected = cableMap[this.cableType] || null

          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Basic ' + btoa(`${username}:${password}`)
            },
            body: JSON.stringify({
              action: 'sendMessage',
              sessionId: this.sessionId,
              chatInput: this.inputText,
              metadata: {
                timestamp: new Date().toISOString(),
                user_email: this.user?.email || null,
                cable_type: cableSelected,
                transaction_id: this.transactionId || null,
                token_selected: this.userToken || null,
                point_name: this.pointName || null,
                position: this.userPosition || null,
                signature: this.signature || false,
                has_credit_card_registered: Boolean(this.user?.default_card) || false,
                has_pix_registered: Boolean(this.user?.pix_identification) || false,
                has_pix_payed: this.pixPayed || false,
                user_with_battery: this.lastTransactionState === TAKE_BATTERY_SUCCESS || false
              }
            })
          })

          if (response?.status >= 400) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const data = await response.json()
          const text = data.output || data.markdown || data.text || 'Desculpe, não consegui processar sua mensagem.'

          this.messages.push({
            text,
            sender: 'bot',
            senderName: 'IA',
            time: this.getCurrentTime()
          })

          this.saveMessages()
        } catch (error) {
          console.error('Erro ao enviar mensagem:', error)
          this.messages.push({
            text: this.$t('chatbot.error_message'),
            sender: 'bot',
            senderName: 'IA',
            time: this.getCurrentTime()
          })
          this.saveMessages()
        } finally {
          this.isLoading = false
          this.$nextTick(() => {
            this.scrollToBottom()
            this.$refs.inputField.focus()
          })
          this.inputText = ''
        }
      },

      getCurrentTime() {
        const now = new Date()
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        const time = now.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit'
        })

        // Comparar apenas as datas (sem horas)
        const messageDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())

        if (messageDate.getTime() === todayDate.getTime()) {
          return time
        } else if (messageDate.getTime() === yesterdayDate.getTime()) {
          return `Ontem, ${time}`
        } else {
          const date = now.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          return `${date}, ${time}`
        }
      },

      scrollToBottom() {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      }
    }
  }
</script>

<style scoped>
  .chat-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    margin: 0 auto;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: white;
    overflow: hidden;
    box-sizing: border-box;
  }

  .chat-header {
    background: linear-gradient(135deg, #ff6600 0%, #154cff 100%);
    color: white;
    padding: 20px;
    text-align: center;
  }

  .chat-header h3 {
    margin: 0 0 5px 0;
    font-size: 20px;
    font-weight: 600;
  }

  .chat-header p {
    margin: 0;
    font-size: 14px;
    opacity: 0.9;
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f8f9fa;
  }

  .message {
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    animation: fadeIn 0.3s ease-in;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .message.user {
    align-items: flex-end;
  }

  .message.bot {
    align-items: flex-start;
  }

  .message-sender {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-bottom: 6px;
    padding: 0 8px;
  }

  .message.user .message-sender {
    text-align: right;
  }

  .message-content {
    max-width: 75%;
    padding: 12px 16px;
    border-radius: 18px;
    word-wrap: break-word;
    line-height: 1.4;
  }

  .message.user .message-content {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-bottom-right-radius: 4px;
  }

  .message.bot .message-content {
    background: white;
    color: #333;
    border: 1px solid #e0e0e0;
    border-bottom-left-radius: 4px;
  }

  .message-time {
    font-size: 11px;
    color: #999;
    margin-top: 4px;
    padding: 0 8px;
  }

  /* Animação de digitação */
  .typing {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 12px 16px !important;
  }

  .typing span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #999;
    animation: bounce 1.4s infinite ease-in-out;
  }

  .typing span:nth-child(1) {
    animation-delay: -0.32s;
  }

  .typing span:nth-child(2) {
    animation-delay: -0.16s;
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: scale(0);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .chat-input {
    display: flex;
    padding: 16px;
    background: white;
    border-top: 1px solid #e0e0e0;
    gap: 8px;
  }

  .chat-input input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 24px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
  }

  .chat-input input:focus {
    border-color: #667eea;
  }

  .chat-input input:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  .chat-input button {
    padding: 12px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 24px;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    transition:
      transform 0.2s,
      opacity 0.2s;
  }

  .chat-input button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  .chat-input button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  /* Scrollbar customizada */
  .chat-messages::-webkit-scrollbar {
    width: 6px;
  }

  .chat-messages::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  .chat-messages::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }

  .chat-messages::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  .message-content pre {
    background: #f5f7fa;
    padding: 12px;
    border-radius: 8px;
    overflow: auto;
    margin-top: 8px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Courier New', monospace;
    font-size: 13px;
  }

  .message-content code {
    background: rgba(27, 31, 35, 0.05);
    padding: 2px 6px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
  }
</style>
